/* eslint-disable max-len */
/**
 * 時刻・期間の純粋関数（React 非依存）
 */

export const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

export const toDateStr = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const parseDateStr = (s: string): Date => {
  const [y, m, d] = s.split('-').map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d);
};

export const addDays = (s: string, days: number): string => {
  const d = parseDateStr(s);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
};

export const dowOf = (s: string): number => parseDateStr(s).getDay();

export const daysBetweenInclusive = (from: string, to: string): number => {
  const a = parseDateStr(from).getTime();
  const b = parseDateStr(to).getTime();
  return Math.round((b - a) / 86400000) + 1;
};

export const dateRange = (from: string, to: string): Array<string> => {
  const out: Array<string> = [];
  const n = daysBetweenInclusive(from, to);
  for (let i = 0; i < n; i += 1) {
    out.push(addDays(from, i));
  }
  return out;
};

/** "M/D(曜)" */
export const shortDateLabel = (s: string): string => {
  const d = parseDateStr(s);
  return `${d.getMonth() + 1}/${d.getDate()}(${DOW_LABELS[d.getDay()]})`;
};

/** "HH:mm" → 0 時からの分。翌日は +1440 */
export const hmToMinutes = (hm: string | null | undefined, nextDay: boolean = false): number => {
  if (!hm) {
    return 0;
  }
  const [h, m] = hm.split(':').map((v) => parseInt(v, 10));
  return h * 60 + m + (nextDay ? 1440 : 0);
};

/** 分 → "HH:mm"（1440 を超えたら翌日扱いで 24 を引く） */
export const minutesToHm = (minutes: number): string => {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
};

/** 分 → "H:mm"（時間量。例 7:30） */
export const minutesToHours = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}:${pad(abs % 60)}`;
};

/** 小数第 3 位の丸め（労務規約項目マスタ）。0 = 切り捨て、1 = 切り上げ、2 = 四捨五入 */
export type RoundMethod = 0 | 1 | 2;

/**
 * 分 → 10進の時間量（例 7.50）
 *
 * 既存画面（サーバの TimeConvertUtil.converttoAttendSetting）と同じ規則にする。
 * 小数は 2 桁固定、3 桁目はマスタの丸め設定に従う。0 分は 0.00。
 */
export const minutesToDecimalHours = (minutes: number, round: number = 0): string => {
  const sign = minutes < 0 ? '-' : '';
  const abs = Math.abs(minutes);
  if (abs === 0) {
    return '0.00';
  }
  // 浮動小数点の誤差を避けるため、100 倍した整数で丸める
  const scaled = (abs * 100) / 60;
  let cents: number;
  if (round === 1) {
    cents = Math.ceil(scaled - 1e-9);
  } else if (round === 2) {
    cents = Math.floor(scaled + 0.5 + 1e-9);
  } else {
    cents = Math.floor(scaled + 1e-9);
  }
  const h = Math.floor(cents / 100);
  const c = cents % 100;
  return `${sign}${h}.${c < 10 ? `0${c}` : c}`;
};

/**
 * 時間量の表記（9/16 追加、9/23 に既存画面と同じ規則へ）
 *
 * sexagesimal = true で 60 進（7:30）、false で 10 進（7.50）。単位は付けない（既存画面と同じ）。
 * どちらを使うかは共通設定（timeFormat）で決め、auto のときは勤怠の労務規約項目マスタに合わせる。
 */
export const formatWorkMinutes = (minutes: number, sexagesimal: boolean, round: number = 0): string => (
  sexagesimal ? minutesToHours(minutes) : minutesToDecimalHours(minutes, round)
);

/** 画面に渡す整形関数 */
export type TimeFormatter = (minutes: number) => string;

/** 60 進か 10 進か・丸めを閉じ込めた整形関数を作る。部品には関数 1 つだけを渡せば済むようにする */
export const makeTimeFormatter = (sexagesimal: boolean, round: number = 0): TimeFormatter => (
  (minutes: number) => formatWorkMinutes(minutes, sexagesimal, round)
);

/** 既定の整形（60 進）。設定を受け取れない部品の既定値に使う */
export const defaultTimeFormatter: TimeFormatter = (minutes: number) => minutesToHours(minutes);

/**
 * 共通設定の timeFormat と労務規約項目マスタから、60 進で出すかどうかを決める
 * timeDisplayFormat: 0 = 10進、1 = 60進
 */
export const resolveSexagesimal = (timeFormat: string | null | undefined, timeDisplayFormat: number | null | undefined): boolean => {
  if (timeFormat === 'hhmm') {
    return true;
  }
  if (timeFormat === 'decimal') {
    return false;
  }
  return timeDisplayFormat !== 0;
};

/** 刻みに丸める（最近傍） */
export const roundToSlot = (minutes: number, slot: number): number => Math.round(minutes / slot) * slot;

/**
 * 開始・終了から拘束時間（分）を返す。終了が開始以下なら翌日扱い
 */
export const spanMinutes = (start: string | null, end: string | null, isEndNextDay: boolean): number => {
  if (!start || !end) {
    return 0;
  }
  const s = hmToMinutes(start);
  let e = hmToMinutes(end, isEndNextDay);
  if (e <= s) {
    e += 1440;
  }
  return e - s;
};

/** 実働（分）= 拘束 − 休憩 */
export const workMinutesOf = (start: string | null, end: string | null, isEndNextDay: boolean, breakMinutes: number | null): number => {
  const span = spanMinutes(start, end, isEndNextDay);
  return Math.max(0, span - (breakMinutes || 0));
};

/** 休憩が分からないときの目安（パレットの表示と同じ。8 時間以上 60 分、6 時間以上 45 分、4 時間超 15 分） */
export const autoBreakMinutes = (spanMin: number): number => {
  if (spanMin >= 480) {
    return 60;
  }
  if (spanMin >= 360) {
    return 45;
  }
  return spanMin > 240 ? 15 : 0;
};

type WorkLike = {
  attendType: number;
  start: string | null;
  end: string | null;
  isEndNextDay: boolean;
  breakMinutes?: number | null;
  workMinutes?: number;
};

/**
 * サーバで勤務時間（と人件費・交通費）を計算済みか（9/25）
 *
 * シフト表は保存では再計算しない（確定して公開したときにまとめて計算する）。
 * 計算されていないシフトは、勤務なのに勤務時間が 0 のまま返ってくる。保存待ちのシフトも未計算として扱う。
 */
export const isWorkCalculated = (s: WorkLike): boolean => s.attendType !== 0 || (s.workMinutes !== undefined && s.workMinutes > 0);

/**
 * 表に出す勤務時間（分）。計算済みならサーバの値、未計算なら開始・終了・休憩からの目安（9/25）
 * 休憩が入っていなければ {@link autoBreakMinutes} を引く。
 */
export const effectiveWorkMinutes = (s: WorkLike): number => {
  if (s.attendType !== 0) {
    return 0;
  }
  if (isWorkCalculated(s)) {
    return s.workMinutes as number;
  }
  const span = spanMinutes(s.start, s.end, s.isEndNextDay);
  const rest = s.breakMinutes && s.breakMinutes > 0 ? s.breakMinutes : autoBreakMinutes(span);
  return Math.max(0, span - rest);
};

/** 週の起点（weekStartDow）に合わせて、date を含む週の開始日 */
export const startOfWeek = (date: string, weekStartDow: number): string => {
  const dow = dowOf(date);
  const diff = (dow - weekStartDow + 7) % 7;
  return addDays(date, -diff);
};

/** 半月: 1〜15 / 16〜末 */
export const halfMonthRange = (date: string): { from: string; to: string } => {
  const d = parseDateStr(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  if (d.getDate() <= 15) {
    return { from: toDateStr(new Date(y, m, 1)), to: toDateStr(new Date(y, m, 15)) };
  }
  return { from: toDateStr(new Date(y, m, 16)), to: toDateStr(new Date(y, m + 1, 0)) };
};

/** 月: monthStartDay を起点にした 1 か月 */
export const monthRange = (date: string, monthStartDay: number): { from: string; to: string } => {
  const d = parseDateStr(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  const startDay = monthStartDay > 1 ? monthStartDay : 1;
  if (startDay === 1) {
    return { from: toDateStr(new Date(y, m, 1)), to: toDateStr(new Date(y, m + 1, 0)) };
  }
  if (d.getDate() >= startDay) {
    return { from: toDateStr(new Date(y, m, startDay)), to: toDateStr(new Date(y, m + 1, startDay - 1)) };
  }
  return { from: toDateStr(new Date(y, m - 1, startDay)), to: toDateStr(new Date(y, m, startDay - 1)) };
};

export type ViewRange = { from: string; to: string };

export const rangeForView = (
  view: 'day' | 'week' | 'half' | 'month' | 'custom', anchor: string, weekStartDow: number, monthStartDay: number,
): ViewRange => {
  switch (view) {
    case 'day':
      return { from: anchor, to: anchor };
    case 'week': {
      const from = startOfWeek(anchor, weekStartDow);
      return { from, to: addDays(from, 6) };
    }
    case 'month':
      return monthRange(anchor, monthStartDay);
    default:
      return halfMonthRange(anchor);
  }
};

/** 前後の期間へ移動したときの基準日 */
export const shiftAnchor = (view: 'day' | 'week' | 'half' | 'month' | 'custom', range: ViewRange, direction: 1 | -1): string => {
  if (view === 'day') {
    return addDays(range.from, direction);
  }
  if (view === 'week') {
    return addDays(range.from, 7 * direction);
  }
  if (view === 'half') {
    return direction > 0 ? addDays(range.to, 1) : addDays(range.from, -1);
  }
  return direction > 0 ? addDays(range.to, 1) : addDays(range.from, -1);
};

/** 任意期間を前後へ動かす。日数を保ったまま平行移動する */
export const shiftCustomRange = (range: ViewRange, direction: 1 | -1): ViewRange => {
  const days = daysBetweenInclusive(range.from, range.to);
  return {
    from: addDays(range.from, days * direction),
    to: addDays(range.to, days * direction),
  };
};

/** ISO 日時 → "M/D HH:mm" */
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** 5 分刻みの時刻選択肢 */
export const timeOptions = (step: number = 5): Array<string> => {
  const out: Array<string> = [];
  for (let m = 0; m < 1440; m += step) {
    out.push(minutesToHm(m));
  }
  return out;
};
